import React, { useState } from 'react';
import { Autocomplete, Box, TextField, IconButton, Typography, Paper } from '@mui/material';
import { FilterFieldSx, DropDownSX, DropDownSXWithColor } from '../../../../constants/style';
import Tooltip from '../../../../utils/Tooltip';
import { CirclePlus, UserPen } from 'lucide-react';
import CommonDialog from '../CommonDialog';
import AddClient from '../../Clients/AddClient';
import { FONTSIZE } from '../../../../constants/style';
import { useResponsive } from '../../../../utils';

const CustomPaper = React.forwardRef((props, ref) => {
  return (
    <Paper ref={ref} {...props}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2.5fr 1fr 1fr',
          gap: 1,
          p: 1,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}
        >
          Client Name
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}
        >
          Area
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}
        >
          City
        </Typography>
      </Box>
      {props.children}
    </Paper>
  );
});

const ChooseClient = ({
  clients = [],
  value,
  onChange,
  clientLoading,
  onBlur,
  error,
  helperText,
  isReadonly = false,
  isClientPermission,
  isEditMode = false,
  fetchClientListResponse,
  editClientDetails,
  customDropDownSX = {},
  isDcrClient = false,
  onKeyDown
}) => {
  const [openDialog, setOpenDialog] = useState(false);

  const handleClickAddClient = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);
  const { isMobile } = useResponsive();

  const handleClientChange = (event, newValue) => {
    if (onChange) {
      onChange(
        newValue?.clientGUID || newValue?.dailyCallClientGUID || 0,
        newValue?.clientName || newValue?.dailyCallClientName || '',
        newValue
      );
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          alignItems: 'flex-start',
          minWidth: 0,
          width: '100%',
          ...customDropDownSX
        }}
      >
        <Autocomplete
          size="small"
          fullWidth
          options={clients}
          value={clients.find((c) => (c.clientGUID || c.dailyCallClientGUID) === value) || null}
          onChange={handleClientChange}
          disablePortal={false}
          getOptionLabel={(option) => option?.clientName || option?.dailyCallClientName || ''}
          isOptionEqualToValue={(option, val) =>
            (option?.clientGUID || option?.dailyCallClientGUID) ===
            (val?.clientGUID || val?.dailyCallClientGUID)
          }
          {...(onKeyDown && { onKeyDown })}
          readOnly={isReadonly}
          loading={clientLoading}
          sx={DropDownSXWithColor}
          slotProps={{
            paper: {
              sx: {
                ...DropDownSX,
                ...(!isMobile && {
                  minWidth: '450px',
                  width: 'max-content'
                })
              }
            },

            listbox: {
              sx: {
                maxHeight: '300px',
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 0.5,

                '& .MuiAutocomplete-option': {
                  paddingRight: '12px'
                },

                '&::-webkit-scrollbar': {
                  width: '5px'
                },

                '&::-webkit-scrollbar-track': {
                  background: 'transparent'
                },

                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '10px'
                },

                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#999'
                },

                scrollbarWidth: 'thin',
                scrollbarColor: '#c1c1c1 transparent'
              }
            },

            ...(!isMobile && {
              popper: {
                placement: 'bottom-start',
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      mainAxis: false
                    }
                  }
                ],
                sx: {
                  width: 'max-content !important',
                  minWidth: '100%'
                }
              }
            })
          }}
          PaperComponent={CustomPaper}
          renderOption={({ key, ...props }, option) => (
            <Box
              component="li"
              key={key}
              {...props}
              sx={{
                ...props.sx,
                display: 'grid !important',
                gridTemplateColumns: '2.5fr 1fr 1fr',
                gap: 1,
                width: '100%',
                borderBottom: '1px solid #f0f0f0',
                px: 1,
                py: 0.8
              }}
            >
              <Typography
                sx={{ fontSize: FONTSIZE }}
                variant="body2"
                noWrap
                title={option.clientName || option.dailyCallClientName || '-'}
              >
                {option.clientName || option.dailyCallClientName || '-'}
              </Typography>
              <Typography
                sx={{ fontSize: FONTSIZE }}
                variant="body2"
                color="text.secondary"
                noWrap
                title={option.area || option.dailyCallArea || '-'}
              >
                {option.area || option.dailyCallArea || '-'}
              </Typography>
              <Typography
                sx={{ fontSize: FONTSIZE }}
                variant="body2"
                color="text.secondary"
                noWrap
                title={option.city || option.dailyCallCity || '-'}
              >
                {option.city || option.dailyCallCity || '-'}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client Name"
              placeholder="Select Client"
              sx={FilterFieldSx}
              onBlur={onBlur}
              error={error}
              helperText={helperText}
            />
          )}
        />
        {!isEditMode && isClientPermission?.hasAddPermission && (
          <Tooltip title="Add New Client">
            <IconButton
              size="small"
              onClick={handleClickAddClient}
              sx={{
                p: 0.5,
                color: 'text.secondary',
                mt: 0.5,
                display: isClientPermission?.hasAddPermission ? 'inline-flex' : 'none'
              }}
            >
              <CirclePlus size={18} />
            </IconButton>
          </Tooltip>
        )}
        {isEditMode && isClientPermission?.hasEditPermission && (
          <Tooltip title="Edit Client">
            <IconButton
              size="small"
              onClick={handleClickAddClient}
              sx={{
                p: 0.5,
                color: 'text.secondary',
                mt: 0.5,
                display: isClientPermission?.hasEditPermission ? 'inline-flex' : 'none'
              }}
            >
              <UserPen size={18} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <CommonDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={isEditMode ? 'Edit Client Details' : 'Add Client'}
        persistent={true}
        fullWidth={true}
        maxWidth={'lg'}
      >
        <AddClient
          isDcrClient={isDcrClient}
          client={isEditMode ? { clientGUID: value, dailyCallClientGUID: value } : null}
          userAccess={isClientPermission}
          onSuccess={() => {
            handleCloseDialog();
            if (fetchClientListResponse) fetchClientListResponse();
            if (editClientDetails) editClientDetails();
          }}
          onCancel={handleCloseDialog}
        />
      </CommonDialog>
    </>
  );
};

export default ChooseClient;
